/** @jsxImportSource @emotion/react */
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useEvent from 'react-use-event-hook';
import { Button } from '@mui/material';
import { CellType } from '@grapecity/wijmo.grid';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import CustomGrid from 'components/grids/CustomGrid';
import { IconButton } from 'components/buttons/IconSVG';
import { SetupPeople, WorkEquipment } from 'models/setup/SetupDailyReport';
import {
  findDailyReportEquipments,
  findDailyReportEquipmentWorkers,
} from 'apis/setup/SetupDailyReport';
import { SetupEquipment } from 'models/setup/SetupEquipment';
import SetupEquipmentPopUp from '../common/SetupEquipmentPopUp';
import { BizName } from 'models/common/Common';
import FileUploadPopUp from '../../common/components/FileUploadPopUp';
import SetupDailyReportWorkerPopup from './SetupDailyReportWorkerPopup';
import ImageSlider from 'components/viewer/ImageSlide';
import { API_BASE_URL } from 'utils/ApiUtil';
import CustomDialog from 'components/modals/common/CustomDialog';
import WJCellTextarea from 'components/inputs/WJCellTextarea';
import { findFileImageList } from 'apis/file/File';
import { FileImgInfo } from 'models/admin/FileImgInfo';
import { useCommonModal } from 'hooks/useCommonModal';
import { useMessageBar } from 'hooks/useMessageBar';

/**
 * @desc 설비셋업관리 > 셋업일보조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.11.22   jd             신규 생성
 * 2026.01.16   jshwang12      설비 작업자 수 변경 시 변경된 합계 적용되도록 수정
 * 2026.01.20   jshwang12      작업자 수 소수점 2자리까지 입력 가능 (관리자, 설비별 작업자 모두), 작업자 컬럼 변경
 */
interface Props {
  readOnly: boolean;
  onChangeWorkers: (result: SetupPeople) => void;
}
const SetupDailyReportEquipmentContent = (props: Props, ref) => {
  const { t } = useTranslation();
  const { openCommonModal } = useCommonModal();
  const { openMessageBar } = useMessageBar();

  const gridRef = useRef<any>();
  const [selectedRow, setSelectedRow] = useState<any>();

  const [isOpenSetupEquipmentModal, setOpenSetupEquipmentModal] = useState<boolean>(false);
  const [setupEquipmentModalCondition, setSetupEquipmentModalCondition] = useState<any>();
  const [isOpenFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);
  const [fileUploadModalCondition, setFileUploadModalCondition] = useState<any>({});
  const [isOpenEquipmentWorkerModal, setOpenEquipmentWorkerModal] = useState<boolean>(false);
  const [equipmentWorkerModalCondition, setEquipmentWorkerModalCondition] = useState<any>({});
  const [rowData, setRowData] = useState<WorkEquipment[]>([]);
  const [condition, setCondition] = useState<WorkEquipment>();
  const [hitTest, setHitTest] = useState<any>();
  const [isOpenCellTextarea, setOpenCellTextarea] = useState<boolean>(false);

  const [isShowImgSlider, setShowImgSlider] = useState<boolean>(false);
  const [imgList, setImgList] = useState<FileImgInfo[]>([]);

  useImperativeHandle(ref, () => ({
    search: (params) => {
      const p = {
        wkId: params?.wkId,
        elmPrjId: params?.elmPrjId,
        excpId: params?.excpId,
      };
      setCondition(p);
      handleSearch(p);
    },
    getRowData: () => {
      return rowData;
    },
  }));

  useEffect(() => {
    gridRef.current?.autoSizeRows();
  }, [rowData]);

  const handleSearch = (params) => {
    if (params?.wkId && params?.elmPrjId && params?.excpId) {
      findDailyReportEquipments(params?.wkId, params?.elmPrjId, params?.excpId).then((result) =>
        setRowData(result)
      );
    } else {
      setRowData([]);
    }
  };

  const FileCellRenderer = (params: any) => {
    const customTag = () => {
      return `
        <div id="fileDiv" class="fileDiv">
          <span><em>${(params.value || 0).toLocaleString()}</em></span>
          <button>미리보기</button>
        </div>
      `;
    };

    return customTag();
  };

  const layoutDefinition = useMemo(() => {
    return [
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        width: 140,
        isReadOnly: true,
        align: 'center',
        cssClass: 'noTooltip',
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        width: 190,
        isReadOnly: true,
        align: 'center',
        cssClass: 'noTooltip',
      },
      {
        binding: 'wkProgCtn',
        header: String(t('setup.label.금일 공사실적', '금일 공사실적')),
        minWidth: 290,
        width: '*',
        align: 'left',
        wordWrap: true,
        multiLine: true,
        cssClass: 'noTooltip',
      },
      {
        binding: 'nextPlanCtn',
        header: String(t('setup.label.명일 공사계획', '명일 공사계획')),
        minWidth: 290,
        width: '*',
        align: 'left',
        wordWrap: true,
        multiLine: true,
        cssClass: 'noTooltip',
      },
      {
        binding: 'sngrMatrCtn',
        header: String(t('setup.label.특이사항 & 비고', '특이사항 & 비고')),
        minWidth: 80,
        width: 190,
        align: 'left',
        wordWrap: true,
        multiLine: true,
        cssClass: 'noTooltip',
      },
      {
        binding: 'inptMnhr',
        header: String(t('setup.label.작업자', '작업자')),
        isReadOnly: true,
        align: 'center',
        minWidth: 50,
        width: 90,
        cssClass: 'noTooltip',
        cellTemplate: (params) =>
          `<div class="fileDiv"><span><em>${(
            (params.item.eqiupmentWorkerList || []).reduce(
              (acc, cur) => acc + Number(cur?.inptMnhr || 0),
              0
            ) || 0
          ).toLocaleString()}</em></span></div>`,
      },
      {
        binding: 'atchFileGrId',
        visible: false,
      },
      {
        binding: 'atchFileCnt',
        header: String(t('setup.label.현장사진', '현장사진')),
        isReadOnly: true,
        align: 'center',
        minWidth: 90,
        width: 130,
        cssClass: 'WijmoBasicButton noTooltip',
        cellTemplate: FileCellRenderer,
      },
    ];
  }, []);

  const getImgList = async (atchFileGrId) => {
    await findFileImageList(atchFileGrId).then((res) => {
      setImgList(res?.map((o) => ({ ...o })) || []);
    });
  };

  const onItemFormatter = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      if ('wkProgCtn' === binding) {
        cell.ariaReadOnly = props?.readOnly;
      }
    }
  });

  const onInitialized = useEvent((grid) => {
    gridRef.current = grid;
    grid.itemFormatter = onItemFormatter;

    grid.selectionChanged.addHandler((s) => {
      const row = s.selection.row;

      if (row >= 0) {
        const item = s.rows[row].dataItem;
        setSelectedRow(item);
      }
    });

    grid.hostElement.addEventListener('click', async (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;
      const binding = grid.columns[ht.col].binding;

      if (ht.panel === grid.cells) {
        if ('atchFileCnt' === binding) {
          if (e.target.tagName !== 'BUTTON') {
            setFileUploadModalCondition({
              atchFileGrId: item?.atchFileGrId,
              atchFileTpCd: 'SETUP_DRPT_EQP',
              tableName: 'tb_eelmb_prj_wk_drpt_eqp_m',
              bizName: BizName.SETUP,
              target: {
                item: item,
                row: ht.row,
                col: ht.col,
              },
            });
            setOpenFileUploadModal(true);
          } else {
            // 현장 이미지 조회
            await getImgList(item?.atchFileGrId);
            /*if (!imgList || imgList.length === 0) {
              openMessageBar({
                type: 'error',
                content: t('admin.msg.첨부 이미지가 없습니다.', '첨부 이미지가 없습니다.'),
              });
              return false;
            }*/

            setShowImgSlider(true);
          }
        }
        if ('inptMnhr' === binding) {
          findDailyReportEquipmentWorkers(item?.wkId, item?.tgtEqpId).then((result) => {
            if ((result || []).length < 1) {
              openMessageBar({
                type: 'warning',
                content: t('setup.label.일정등록 안된 설비입니다', '일정등록 안된 설비입니다'),
              });
              return;
            }
            setEquipmentWorkerModalCondition({
              wkId: item.wkId,
              tgtEqpId: item.tgtEqpId,
              eqpId: item.eqpId,
              eqpNm: item.eqpNm,
              eqiupmentWorkerList:
                (item.eqiupmentWorkerList || []).length < 1 ? result : item.eqiupmentWorkerList,
              target: {
                item: item,
                row: ht.row,
                col: ht.col,
              },
            });
            setOpenEquipmentWorkerModal(true);
          });
        }
        /*// 금일 공사실적
        if ('wkProgCtn' === binding) {
          setHitTest(ht);
          setOpenCellTextarea(true);
        }
        // 명일 공사계획
        if ('nextPlanCtn' === binding) {
          setHitTest(ht);
        }*/
      }
    });
  });

  const handleOpenSetupEquipmentPopup = () => {
    if (condition?.elmPrjId && condition?.excpId) {
      setSetupEquipmentModalCondition({
        elmPrjId: condition?.elmPrjId,
        excpId: condition?.excpId,
      });
      setOpenSetupEquipmentModal(true);
    }
  };

  return (
    <>
      <SubTitleLayout>
        <SubTitleGroup>
          <h3>{t('setup.label.작업 설비', '작업 설비')}</h3>
          <span className="total">
            {t('setup.label.총', '총')}
            <span>{(rowData || []).length.toLocaleString()}</span>
            {t('setup.label.건', '건')}
          </span>
          {!props?.readOnly && (
            <div className="info warning">
              {t(
                `setup.label.텍스트 입력 중 줄바꿈은 'Alt+Enter' 입력으로 해주세요.`,
                `텍스트 입력 중 줄바꿈은 'Alt+Enter' 입력으로 해주세요.`
              )}
            </div>
          )}
        </SubTitleGroup>
        {!props?.readOnly && (
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="icoNone"
              onClick={handleOpenSetupEquipmentPopup}
            >
              {t('setup.label.작업설비 선택', '작업설비 선택')}
            </Button>
          </ControlBtnGroup>
        )}
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={layoutDefinition}
        rowData={rowData}
        initialized={onInitialized}
        isSelector={false}
        isReadOnly={props?.readOnly}
        allowPinning={false}
        height={(rowData || []).length > 0 ? 0 : 200} // [26.03.04] 데이터 있는 경우 스크롤없이 전체 노출 (데이터 없는 경우 최소 높이 200 설정)
        onFilterChange={(s, e) => {
          gridRef.current?.autoSizeRows();
        }}
        sortedColumn={(grid, e) => {
          grid.autoSizeRows();
        }}
        resizedColumn={(grid, e) => {
          grid.autoSizeRows();
        }}
        cellEditEnded={(grid, e) => {
          grid.autoSizeRow(e.row);
        }}
      />

      {isOpenCellTextarea && (
        <WJCellTextarea
          grid={gridRef.current}
          hitTest={hitTest}
          isReadOnly={props?.readOnly}
          close={() => {
            setOpenCellTextarea(false);
            gridRef.current.autoSizeRow(hitTest.row);
          }}
        />
      )}

      {isOpenSetupEquipmentModal && (
        <SetupEquipmentPopUp
          open={isOpenSetupEquipmentModal}
          close={() => setOpenSetupEquipmentModal(false)}
          title={t('setup.label.설비추가', '설비추가')}
          condition={{
            elmPrjId: setupEquipmentModalCondition?.elmPrjId,
            excpId: setupEquipmentModalCondition?.excpId,
          }}
          singleSelect={false}
          defaultEquipment={rowData}
          onCallback={(result) => {
            setRowData(
              ((result || []) as SetupEquipment[]).reduce(
                (acc, cur) => [
                  ...acc,
                  {
                    ...cur,
                    wkId: condition?.wkId,
                  },
                ],
                [] as WorkEquipment[]
              )
            );
          }}
        />
      )}
      {isOpenFileUploadModal && (
        <FileUploadPopUp
          open={isOpenFileUploadModal}
          close={() => setOpenFileUploadModal(false)}
          onlyImages={true}
          downloadOnly={props?.readOnly}
          initParam={{
            atchFileGrId: fileUploadModalCondition.atchFileGrId,
            atchFileTpCd: fileUploadModalCondition.atchFileTpCd,
            optValCtn1: fileUploadModalCondition.tableName,
            bizName: fileUploadModalCondition.bizName,
          }}
          onCallback={(atchFileGrId, fileCount) => {
            const target = fileUploadModalCondition.target;
            if (target) {
              const item = gridRef.current?.rows[target.row].dataItem;
              item.atchFileGrId = atchFileGrId;
              gridRef.current?.setCellData(target.row, target.col, fileCount);
            }
            setOpenFileUploadModal(false);
          }}
        />
      )}
      {isOpenEquipmentWorkerModal && (
        <SetupDailyReportWorkerPopup
          open={isOpenEquipmentWorkerModal}
          close={() => setOpenEquipmentWorkerModal(false)}
          rowData={equipmentWorkerModalCondition?.eqiupmentWorkerList}
          condition={equipmentWorkerModalCondition}
          isReadOnly={props?.readOnly}
          onConfirm={(result) => {
            const summaryWorker = (rows) => {
              return (rows || []).reduce((acc, cur) => {
                if ((cur?.dataItem?.eqiupmentWorkerList || []).length > 0) {
                  const eqp = (cur?.dataItem?.eqiupmentWorkerList || []).reduce((ea, ec) => {
                    let key = '';
                    if (ec.prjSpDivsCd === 'S04') {
                      // 제어
                      key = 'ctrlCnt';
                    } else if (ec.prjSpDivsCd === 'S02') {
                      // 기구
                      key = 'machCnt';
                    } else if (ec.prjSpDivsCd === 'S01') {
                      // 운송
                      key = 'trspCnt';
                    } else if (ec.prjSpDivsCd === 'S03') {
                      // 전장
                      key = 'elpwCnt';
                    }
                    if (key) {
                      ea[key] = String(Number(ea[key] || 0) + Number(ec.inptMnhr || 0));
                    }
                    return ea;
                  }, {} as SetupPeople);
                  acc['ctrlCnt'] = String(
                    Number(acc['ctrlCnt'] || 0) + Number(eqp['ctrlCnt'] || 0)
                  );
                  acc['machCnt'] = String(
                    Number(acc['machCnt'] || 0) + Number(eqp['machCnt'] || 0)
                  );
                  acc['trspCnt'] = String(
                    Number(acc['trspCnt'] || 0) + Number(eqp['trspCnt'] || 0)
                  );
                  acc['elpwCnt'] = String(
                    Number(acc['elpwCnt'] || 0) + Number(eqp['elpwCnt'] || 0)
                  );
                }
                return acc;
              }, {} as SetupPeople);
            };
            const target = equipmentWorkerModalCondition.target;
            if (target) {
              const item = gridRef.current?.rows[target.row].dataItem;
              item.eqiupmentWorkerList = result;
            }
            props?.onChangeWorkers?.(summaryWorker(gridRef.current?.rows));
            gridRef.current?.refresh();
          }}
        />
      )}
      <CustomDialog
        title={`${selectedRow?.eqpNm ?? ''}`}
        open={isShowImgSlider}
        onClose={() => setShowImgSlider(false)}
      >
        <ImageSlider
          images={imgList.map((o) => {
            const url = `${API_BASE_URL}/v1/file-image/view`;

            const params = new URLSearchParams({
              atchFileGrId: o.atchFileGrId ?? undefined,
              atchFileId: o.atchFileId ?? undefined,
            });

            return {
              img: params.toString() ? `${url}?${params.toString()}` : url,
              date: o.dataInsDtm ?? null,
            };
          })}
          alt={`${selectedRow?.eqpId ?? 'No'} image`}
        />
      </CustomDialog>
    </>
  );
};

export default React.forwardRef(SetupDailyReportEquipmentContent);
